import { parseDateTime } from 'utils/dates/date.utils';

// The API sends utc_time as a zone-less "YYYY-MM-DD HH:MM:SS" string that is already UTC.
// This matches that exact shape so it can be tagged as UTC before it is parsed.
const ZONELESS_UTC_PATTERN = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2})?$/;

// city_id is CustomWeather's internal identifier rather than a place, so a location with no known
// name gets a neutral label instead of a number the user cannot recognize.
const UNNAMED_LOCATION_LABEL = 'Location unavailable';

/**
 * Turns one Fire Danger timestamp into a Date, or null when it cannot be read.
 *
 * A zone-less timestamp is read as local time by Chrome and rejected outright by Safari, so every
 * reported time would shift by the viewer's UTC offset or disappear. Tagging the backend's UTC
 * shape with "Z" keeps the instant the API sent, matching how Current Conditions normalizes the
 * same backend field.
 */
const parseFireDangerTimestamp = (timestamp) => {
  if (typeof timestamp !== 'string') return parseDateTime(timestamp);

  const safeTimestamp = timestamp.trim();
  return parseDateTime(
    ZONELESS_UTC_PATTERN.test(safeTimestamp)
      ? `${safeTimestamp.replace(' ', 'T')}Z`
      : safeTimestamp,
  );
};

/**
 * Returns the moment a Fire Danger report describes, or null when it has no usable timestamp.
 *
 * Detailed reports carry an unambiguous iso8601 value, so it is preferred. The extended endpoint
 * projects only seven columns, leaving utc_time as the one timestamp it can be read from.
 */
export const getFireDangerReportDate = (report) =>
  parseFireDangerTimestamp(report?.iso8601) ?? parseFireDangerTimestamp(report?.utc_time);

/**
 * Returns the value reports are grouped by, which is empty when a report has no location.
 */
const getFireDangerLocationId = (report) => String(report?.city_id ?? '').trim();

/**
 * Collects the place name for each city_id from any report that carries one.
 *
 * The detailed endpoint selects every column, so its reports include city_name. The extended
 * endpoint projects seven columns without it, so a name learned from one payload is the only way
 * to label the same city in the other. The first name found wins; later rows repeat it.
 */
export const getFireDangerLocationNames = (reports) => {
  const names = new Map();

  (Array.isArray(reports) ? reports : []).filter(Boolean).forEach((report) => {
    const locationId = getFireDangerLocationId(report);
    const name = String(report.city_name ?? report.name ?? '').trim();

    if (name && !names.has(locationId)) names.set(locationId, name);
  });

  return names;
};

/**
 * Groups Fire Danger cards by location, then orders each location by date and time.
 * Compares numeric location IDs naturally, so 2 comes before 10.
 * Missing locations go last; missing dates go last within their location.
 * Filters out empty entries and sorts a new array so cached API data stays untouched.
 */
export const getSortedFireDangerReports = (reports) => {
  // Return no cards while API data is missing or is not a list.
  if (!Array.isArray(reports)) return [];

  return reports.filter(Boolean).sort((firstReport, secondReport) => {
    // Treat numeric and text IDs alike. Use an empty string for missing IDs and remove extra spaces.
    const firstLocation = getFireDangerLocationId(firstReport);
    const secondLocation = getFireDangerLocationId(secondReport);

    // Keep reports with a known location before reports whose location is missing.
    if (!firstLocation && secondLocation) return 1;
    if (firstLocation && !secondLocation) return -1;

    // numeric: true compares number parts by value, so location "2" comes before "10".
    const locationOrder = firstLocation.localeCompare(secondLocation, undefined, { numeric: true });
    // Different locations are already in order; only matching locations need a date comparison.
    if (locationOrder !== 0) return locationOrder;

    // Convert the full timestamps into Date objects; missing or invalid timestamps become null.
    const firstDate = getFireDangerReportDate(firstReport);
    const secondDate = getFireDangerReportDate(secondReport);

    // Put undated reports last within their location. Keep their order when both dates are missing.
    if (!firstDate) return secondDate ? 1 : 0;
    if (!secondDate) return -1;

    // getTime returns milliseconds since January 1, 1970. An earlier first date gives a negative
    // difference, placing it first and keeping both the calendar date and time of day in order.
    return firstDate.getTime() - secondDate.getTime();
  });
};

/**
 * Builds the location and date sections shown above the forecast cards.
 * Uses local calendar dates so each report appears under the same date the user sees.
 * Keeps every report, including multiple reports with the same time-of-day label.
 *
 * knownLocationNames carries city_id to place-name pairs learned from another payload, so a
 * section whose projection omits city_name can still be headed by a name the user recognizes.
 */
export const getFireDangerLocations = (reports, knownLocationNames = new Map()) => {
  // Maps let us collect matching locations and dates without searching the whole list each time.
  // Adding already-sorted reports also keeps the resulting groups in display order.
  const locations = new Map();
  // Names carried by these reports win; names learned elsewhere fill in the rest.
  const locationNames = new Map([...knownLocationNames, ...getFireDangerLocationNames(reports)]);

  getSortedFireDangerReports(reports).forEach((report) => {
    const locationId = getFireDangerLocationId(report);
    if (!locations.has(locationId)) {
      locations.set(locationId, {
        id: locationId,
        name: locationNames.get(locationId) || UNNAMED_LOCATION_LABEL,
        days: new Map(),
      });
    }

    const location = locations.get(locationId);
    const date = getFireDangerReportDate(report);
    // Use the local year, month and day instead of the UTC date, which can be a different day.
    const dayKey = date
      ? `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`
      : 'date-unavailable';

    if (!location.days.has(dayKey)) {
      location.days.set(dayKey, { key: dayKey, date, reports: [] });
    }
    location.days.get(dayKey).reports.push(report);
  });

  // Convert the Maps to arrays so React can render a section for each location and day.
  return Array.from(locations.values(), (location) => ({
    ...location,
    days: Array.from(location.days.values()),
  }));
};

/**
 * Gives detailed Fire Danger cards a readable time-of-day heading from the report's timestamp.
 * Uses the viewer's local time, matching the exact date shown in the card.
 * Morning starts at midnight, afternoon at noon, evening at 6pm, and night at 10pm.
 * @param {object} report A fire danger row; its instant comes from getFireDangerReportDate.
 */
export const getFireDangerTimeOfDay = (report) => {
  const reportDate = getFireDangerReportDate(report);
  if (!reportDate) return 'Time unavailable';

  const hour = reportDate.getHours();
  if (hour < 12) return 'Morning';
  if (hour < 18) return 'Afternoon';
  if (hour < 22) return 'Evening';
  return 'Night';
};
